import { Component, Ref, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { disableBodyScrolling } from '@/utils/disableBodyScrolling';

import { debounce } from 'lodash';

@Component
export class PredictiveSearch extends ThemeComponent {
    @Ref() searchInput!: HTMLInputElement;
    @Ref() searchResults!: HTMLDivElement;
    @Ref closeSearchButton!: HTMLButtonElement;
    @Prop({default: window.scrollY}) scrollPosition!: number;

    init() {
        // Give initial value for scrollPosition
        this.scrollPosition = window.scrollY;

        this.searchInput.addEventListener('input',
            debounce(() => {
                this.onChange();
            }, 300)
        );

        // Add global ESC key listener to close search
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.getAttribute('open') === 'true') {
                this.scrollOccured();
                document.body.classList.remove('search-open');
                this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);
                this.closeSearchButton.classList.add('visually-hidden');
            }
        });

        this.closeSearchButton.classList.add('visually-hidden');
    }

    onChange() {
        const searchTerm: string = this.searchInput.value.trim();

        if(!searchTerm.length) {
            this.closeSearchButton.classList.add('visually-hidden');
            this.clearResults();
            this.closeResults();
            return;
        }

        this.getResults(searchTerm);
    }

    appendResults(text: string): void {
        const format: Document = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup: string | undefined =
            format.querySelector('#shopify-section-predictive-search')?.innerHTML || undefined;

        if(resultsMarkup === undefined) {
            return;
        }

        this.searchResults.innerHTML = resultsMarkup;
    }

    clearResults(): void {
        const results: HTMLDivElement | null = document.querySelector('#predictive-search-results') || null;

        if(results) {
            results.remove();
        }
    }

    openResults(): void {
        this.setAttribute('open', 'true');
        document.body.classList.add('search-open');
        this.scrollPosition = disableBodyScrolling(true, this.scrollPosition);

        this.closeSearchButton.classList.remove('visually-hidden');

        this.$listen(this.closeSearchButton, 'click', () => {
            this.scrollOccured();
            document.body.classList.remove('search-open');
            this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);
            this.closeSearchButton.classList.add('visually-hidden');
        });

        this.closeOnOutsideClick();

    }

    closeResults(): void {
        this.setAttribute('open', 'false');
    }

    scrollOccured() {
        this.searchInput.value = "";
        this.clearResults();
        this.closeResults();
    }

    getResults(searchTerm: string): void {
        const url: string =
        // eslint-disable-next-line max-len
        `/search/suggest?q=${searchTerm}&resources[type]=product,page,article,collection&resources[options][unavailable_products]=show&resources[limit]=100&resources[options][fields]=title,product_type,variants.title,variants.barcode,variants.sku&resources[limit_scope]=each&section_id=predictive-search`;

        fetch(url)
            .then((response) => {
                if(!response.ok) {
                    throw new Error(`${response.status}`);
                }
                return response.text();
            })
            .then((text) => {
                this.appendResults(text);
                this.openResults();
            })
            .catch((error) => {
                throw error;
            });
    }

    closeOnOutsideClick() {
        document.addEventListener('click', (event) => {
            const target = event.target as Node;
            if (!this.searchResults.contains(target) && !this.searchInput.contains(target)) {
                this.scrollOccured();
                this.closeSearchButton.classList.add('visually-hidden');
            }
            document.body.classList.remove('search-open');
            this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);
        });
    }
}

